import { useSubscription } from "./useSubscription";
import { useFamilyEntitlement, useFamilyEntitlementsMap } from "./useFamilyEntitlement";

export type Entitlements = {
  hasPlus: boolean;
  hasTogether: boolean;
  familyPlan: "free" | "together";
  maxChildren: number;
  maxHistoryDays: number;
};

export function useEntitlements(familyId: string | null) {
  const sub = useSubscription();
  const fam = useFamilyEntitlement(familyId);

  // User plan: free or plus (together on user level maps to plus for entitlements)
  const userPlan = sub.plan === "together" ? "plus" : sub.plan;
  // Sammen kun for denne familien: enten familien har Sammen (noen har kjøpt), eller jeg har egen Sammen
  const hasTogether = fam.isTogetherFamily || sub.plan === "together";
  // hasPlus: enten bruker har eget abonnement, eller familien har Sammen (medforelder arver tilgang)
  const hasPlus = userPlan !== "free" || hasTogether;

  return {
    userPlan: userPlan as "free" | "plus",
    familyPlan: fam.familyPlan,
    hasPlus,
    hasTogether,
    isLoading: sub.isLoading || fam.isLoading,
    isTrialing: sub.isTrialing,
    trialEndsAt: sub.trialEndsAt,
    maxChildren: Infinity,
    maxHistoryDays: hasPlus ? Infinity : 7,
  };
}

/** For views that show multiple children (possibly from different families). Returns entitlements per child/family. */
export function useEntitlementsForChildren(
  children: Array<{ id: string; family_id: string }>
) {
  const sub = useSubscription();
  const familyIds = Array.from(new Set(children.map((c) => c.family_id).filter(Boolean)));
  const { familyPlanMap, isLoading: mapLoading } = useFamilyEntitlementsMap(familyIds);

  const userPlan = sub.plan === "together" ? "plus" : sub.plan;

  function getEntitlementsForFamily(familyId: string | null): Entitlements {
    const familyPlan = (familyId && familyPlanMap[familyId]) || "free";
    const hasTogether = familyPlan === "together" || sub.plan === "together";
    const hasPlus = userPlan !== "free" || hasTogether;
    return {
      hasPlus,
      hasTogether,
      familyPlan,
      maxChildren: Infinity,
      maxHistoryDays: hasPlus ? Infinity : 7,
    };
  }

  function getEntitlementsForChild(child: { id: string; family_id: string }): Entitlements {
    return getEntitlementsForFamily(child.family_id);
  }

  return {
    hasPlus: userPlan !== "free",
    getEntitlementsForFamily,
    getEntitlementsForChild,
    isLoading: sub.isLoading || mapLoading,
  };
}
